import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { toast } from "sonner";
import { 
  History, 
  Copy, 
  Check,
  Clock,
  MessageSquare,
  Filter,
  Mail,
  Send,
  Star,
  StarOff,
  RefreshCw,
  Trash2,
  Search,
  ChevronDown,
  Zap
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import EmailComposer from "./EmailComposer";

const TONE_COLORS = {
  firm: "bg-red-500/20 text-red-400",
  friendly: "bg-green-500/20 text-green-400",
  formal: "bg-blue-500/20 text-indigo-400"
};

export default function MessageHistory() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [copied, setCopied] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [emailContent, setEmailContent] = useState("");
  const [favorites, setFavorites] = useState(() => {
    // Load favorites from localStorage
    try {
      return JSON.parse(localStorage.getItem('clientkit_favorites') || '[]');
    } catch {
      return [];
    }
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [messagesRes, projectsRes, clientsRes] = await Promise.all([
          api.get("/messages"),
          api.get("/projects"),
          api.get("/clients")
        ]);
        setMessages(messagesRes.data);
        setProjects(projectsRes.data);
        setClients(clientsRes.data);
      } catch (e) {
        console.error("Error fetching data:", e);
        toast.error("Failed to load message history");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Save favorites to localStorage when they change
  useEffect(() => {
    localStorage.setItem('clientkit_favorites', JSON.stringify(favorites));
  }, [favorites]);

  const handleCopy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      toast.success("Copied! Drop this into your email or DM.");
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(id);
      toast.success("Copied! Drop this into your email or DM.");
      setTimeout(() => setCopied(null), 2000);
    }
  };

  const handleOpenInEmail = (text) => {
    const subjectMatch = text.match(/Subject:\s*(.+?)(?:\n|$)/i);
    const subject = subjectMatch ? subjectMatch[1].trim() : "Client Reply";
    let body = text.replace(/Subject:\s*.+?\n/i, '').trim();
    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
    toast.success("Opening email client...");
  };

  const handleStatusUpdate = async (messageId, newStatus) => {
    try {
      await api.patch(`/messages/${messageId}/status?status=${newStatus}`);
      setMessages(messages.map(m => 
        m.id === messageId ? {...m, status: newStatus} : m
      ));
      toast.success(`Marked as ${newStatus}`);
    } catch (e) {
      console.error("Error updating status:", e);
      toast.error("Failed to update status");
    }
  };

  const handleToggleFavorite = (messageId) => {
    setFavorites(prev => {
      if (prev.includes(messageId)) {
        toast.success("Removed from favorites");
        return prev.filter(id => id !== messageId);
      } else {
        toast.success("Added to favorites! Find it quickly next time.");
        return [...prev, messageId];
      }
    });
  };

  const handleReuseMessage = (message) => {
    // Navigate to Client Firewall with pre-filled data
    navigate('/firewall', { 
      state: { 
        reuseMessage: {
          situation: message.situation,
          tone: message.tone,
          context: message.context || {}
        }
      } 
    });
    toast.success("Template loaded! Edit and regenerate as needed.");
  };

  const handleDelete = async (messageId) => {
    if (!confirm("Delete this message from history?")) return;
    
    try {
      await api.delete(`/messages/${messageId}`);
      setMessages(messages.filter(m => m.id !== messageId));
      setFavorites(prev => prev.filter(id => id !== messageId));
      toast.success("Message deleted");
    } catch (e) {
      console.error("Error deleting message:", e);
      toast.error("Failed to delete message");
    }
  };

  const getProjectTitle = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project?.title || "Unlinked";
  };

  const getClientName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return null;
    const client = clients.find(c => c.id === project.client_id);
    return client?.name || null;
  };

  // Filter messages
  const filteredMessages = messages.filter(m => {
    // Tone filter
    if (filter !== "all" && m.tone !== filter) return false;
    
    // Favorites filter
    if (showFavoritesOnly && !favorites.includes(m.id)) return false;
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSituation = m.situation?.toLowerCase().includes(query);
      const matchesResponse = m.ai_response?.toLowerCase().includes(query);
      const matchesProject = getProjectTitle(m.project_id).toLowerCase().includes(query);
      if (!matchesSituation && !matchesResponse && !matchesProject) return false;
    }
    
    return true;
  });

  // Sort: favorites first, then by date
  const sortedMessages = [...filteredMessages].sort((a, b) => {
    const aFav = favorites.includes(a.id);
    const bFav = favorites.includes(b.id);
    if (aFav && !bFav) return -1;
    if (!aFav && bFav) return 1;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="message-history-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-indigo-500">Message History</h1>
          <p className="text-indigo-400">Your personal playbook of client replies</p>
        </div>
        
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search messages..."
              className="pl-9 pr-4 py-2 w-48 bg-slate-800/50 border border-slate-600 rounded-lg text-indigo-500 text-sm placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none"
            />
          </div>
          
          {/* Favorites Toggle */}
          <button
            onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
              showFavoritesOnly 
                ? 'bg-indigo-500/20 border-indigo-500/50 text-indigo-400' 
                : 'bg-slate-800/50 border-slate-600 text-indigo-400 hover:text-slate-600'
            }`}
          >
            <Star className={`w-4 h-4 ${showFavoritesOnly ? 'fill-stone-400' : ''}`} />
            <span className="text-sm">Favorites</span>
            {favorites.length > 0 && (
              <span className="text-xs bg-slate-400 px-1.5 py-0.5 rounded">
                {favorites.length}
              </span>
            )}
          </button>
          
          {/* Tone Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-indigo-400" />
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="all" className="text-slate-400">All Tones</SelectItem>
                <SelectItem value="firm" className="text-slate-400">Firm</SelectItem>
                <SelectItem value="friendly" className="text-slate-400">Friendly</SelectItem>
                <SelectItem value="formal" className="text-slate-400">Formal</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-slate-800/30 rounded-xl p-4 border border-slate-700/50">
          <p className="text-2xl font-bold text-indigo-500">{messages.length}</p>
          <p className="text-xs text-indigo-400">Total Messages</p>
        </div>
        <div className="bg-slate-800/30 rounded-xl p-4 border border-slate-700/50">
          <p className="text-2xl font-bold text-indigo-400">{favorites.length}</p>
          <p className="text-xs text-indigo-400">Favorites</p>
        </div>
        <div className="bg-slate-800/30 rounded-xl p-4 border border-slate-700/50">
          <p className="text-2xl font-bold text-green-400">{messages.filter(m => m.status === 'sent').length}</p>
          <p className="text-xs text-indigo-400">Sent</p>
        </div>
        <div className="bg-slate-800/30 rounded-xl p-4 border border-slate-700/50">
          <p className="text-2xl font-bold text-indigo-400">{messages.filter(m => m.status === 'draft').length}</p>
          <p className="text-xs text-indigo-400">Drafts</p>
        </div>
      </div>

      {/* Messages List */}
      {sortedMessages.length > 0 ? (
        <div className="space-y-4">
          {sortedMessages.map((message) => {
            const isFavorite = favorites.includes(message.id);
            const clientName = getClientName(message.project_id);
            
            return (
              <Card 
                key={message.id} 
                className={`bg-slate-800/50 border-slate-700/50 hover:border-slate-600 transition-colors ${
                  isFavorite ? 'border-l-4 border-l-indigo-500/50' : ''
                }`}
              >
                <CardContent className="p-5">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleToggleFavorite(message.id)}
                        className={`p-2 rounded-lg transition-colors ${
                          isFavorite 
                            ? 'text-indigo-400 bg-indigo-500/20 hover:bg-slate-400/30' 
                            : 'text-slate-400 hover:text-indigo-400 hover:bg-slate-400'
                        }`}
                        title={isFavorite ? "Remove from favorites" : "Add to favorites"}
                        data-testid={`favorite-${message.id}`}
                      >
                        <Star className={`w-5 h-5 ${isFavorite ? 'fill-stone-400' : ''}`} />
                      </button>
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge className={TONE_COLORS[message.tone]}>
                            {message.tone}
                          </Badge>
                          <Badge variant="outline" className={`text-xs ${
                            message.status === 'sent' 
                              ? 'border-green-500/30 text-green-400' 
                              : 'border-slate-600 text-indigo-400'
                          }`}>
                            {message.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-xs text-slate-400">
                            Project: {getProjectTitle(message.project_id)}
                          </p>
                          {clientName && (
                            <p className="text-xs text-slate-600">
                              • Client: {clientName}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {/* Reuse Button */}
                      <button
                        onClick={() => handleReuseMessage(message)}
                        className="flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-400 px-2 py-1.5 bg-indigo-500/10 hover:bg-indigo-500/20 rounded-lg transition-colors"
                        title="Reuse this template"
                        data-testid={`reuse-${message.id}`}
                      >
                        <Zap className="w-3 h-3" />
                        Reuse
                      </button>
                      
                      {/* Compose Email */}
                      <button
                        onClick={() => {
                          setEmailContent(message.ai_response);
                          setShowEmailComposer(true);
                        }}
                        className="flex items-center gap-1 text-xs text-green-400 hover:text-green-300 px-2 py-1.5 bg-green-500/10 hover:bg-green-500/20 rounded-lg transition-colors"
                        title="Compose email"
                        data-testid={`compose-email-${message.id}`}
                      >
                        <Send className="w-3 h-3" />
                        Compose
                      </button>
                      
                      {/* Quick Copy */}
                      <button
                        onClick={() => handleCopy(message.ai_response, message.id)}
                        className={`p-2 rounded-lg transition-colors ${
                          copied === message.id 
                            ? 'text-green-400 bg-green-500/20' 
                            : 'text-indigo-400 hover:bg-slate-400'
                        }`}
                        title="Copy & Send"
                      >
                        {copied === message.id ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                      
                      {/* Email Client */}
                      <button
                        onClick={() => handleOpenInEmail(message.ai_response)}
                        className="p-2 hover:bg-slate-400 rounded-lg transition-colors"
                        title="Open in email client"
                        data-testid={`email-msg-${message.id}`}
                      >
                        <Mail className="w-4 h-4 text-indigo-400" />
                      </button>
                      
                      {/* Mark Sent */}
                      {message.status === 'draft' && (
                        <button
                          onClick={() => handleStatusUpdate(message.id, 'sent')}
                          className="text-xs text-indigo-400 hover:text-indigo-400 px-2 py-1.5 bg-indigo-500/10 rounded-lg transition-colors"
                        >
                          Mark Sent
                        </button>
                      )}
                      
                      {/* Delete */}
                      <button
                        onClick={() => handleDelete(message.id)}
                        className="p-2 hover:bg-red-500/20 rounded-lg transition-colors text-slate-400 hover:text-red-400"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Situation */}
                  <div className="mb-4">
                    <h4 className="text-xs font-medium text-indigo-400 mb-1">Situation</h4>
                    <p className="text-sm text-slate-400 bg-slate-900/30 p-3 rounded-lg">
                      {message.situation}
                    </p>
                  </div>

                  {/* Response */}
                  <div>
                    <h4 className="text-xs font-medium text-indigo-400 mb-1">Generated Reply</h4>
                    <div className="relative">
                      <pre 
                        className={`text-sm text-slate-600 bg-slate-900/30 p-3 rounded-lg whitespace-pre-wrap font-sans ${
                          expandedId === message.id ? '' : 'line-clamp-4'
                        }`}
                      >
                        {message.ai_response}
                      </pre>
                      {message.ai_response.length > 300 && (
                        <button
                          onClick={() => setExpandedId(expandedId === message.id ? null : message.id)}
                          className="text-xs text-indigo-400 hover:text-indigo-400 mt-2 flex items-center gap-1"
                        >
                          <ChevronDown className={`w-3 h-3 transition-transform ${expandedId === message.id ? 'rotate-180' : ''}`} />
                          {expandedId === message.id ? 'Show less' : 'Show more'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="mt-4 pt-3 border-t border-slate-700 flex items-center justify-between">
                    <p className="text-xs text-slate-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(message.created_at).toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-600">
                      ID: {message.id.slice(0, 8)}...
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="py-12 text-center">
            {showFavoritesOnly ? (
              <>
                <Star className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-400 mb-2">No favorites yet</h3>
                <p className="text-slate-400 mb-4">
                  Star your best replies to build your personal playbook
                </p>
                <button
                  onClick={() => setShowFavoritesOnly(false)}
                  className="text-indigo-400 hover:text-indigo-400 text-sm"
                >
                  Show all messages
                </button>
              </>
            ) : searchQuery ? (
              <>
                <Search className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-400 mb-2">No matches found</h3>
                <p className="text-slate-400">
                  Try a different search term
                </p>
              </>
            ) : (
              <>
                <History className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-400 mb-2">No messages yet</h3>
                <p className="text-slate-400 mb-4">
                  {filter !== "all" 
                    ? `No ${filter} messages found. Try a different filter.` 
                    : "Generated replies will appear here"}
                </p>
                <button
                  onClick={() => navigate('/firewall')}
                  className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-xl transition-colors"
                >
                  <Zap className="w-4 h-4" />
                  Generate Your First Reply
                </button>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Email Composer Modal */}
      <EmailComposer
        isOpen={showEmailComposer}
        onClose={() => setShowEmailComposer(false)}
        initialContent={emailContent}
        clients={clients}
      />
    </div>
  );
}
